// Career paths shown in step 3, plus job-title → path suggestion.
// Defaults extracted verbatim; an admin can override via the "paths" setting.

import { getAdminToken, getSetting, getPublic, putSetting } from "../lib/api";

export interface CareerPath {
  id: string;
  icon: string;
  ar: string;
  en: string;
  descAr: string;
  descEn: string;
}

export const DEFAULT_CAREER_PATHS: CareerPath[] = [
  { id: "finance", icon: "💰", ar: "المالية والمحاسبة", en: "Finance & Accounting", descAr: "المحاسبة، التقارير المالية، التحليل المالي، والتخطيط", descEn: "Accounting, financial reporting, financial analysis, and planning" },
  { id: "procurement", icon: "📦", ar: "المشتريات والعقود", en: "Procurement & Contracts", descAr: "طلبات الشراء، إدارة الموردين، والتعاقدات", descEn: "Purchase requisitions, vendor management, and contracts" },
  { id: "project-management", icon: "🏗️", ar: "إدارة المشاريع والهندسة والتطوير", en: "Projects, Engineering & Development", descAr: "تسليم المشاريع، الهندسة المعمارية والمدنية، التصميم، والتنسيق التشغيلي", descEn: "Project delivery, architecture & civil engineering, design, and operational coordination" },
  { id: "assets-management", icon: "🏢", ar: "إدارة الأصول والمرافق", en: "Assets & Facility Management", descAr: "إدارة العقارات، الصيانة، الأمن والسلامة، والمرافق", descEn: "Real estate, maintenance, safety & security, and facilities" },
  { id: "hr-admin", icon: "👥", ar: "الموارد البشرية والشؤون الإدارية", en: "HR & Administrative Affairs", descAr: "شؤون الموظفين، التوظيف، الشؤون الإدارية والحكومية", descEn: "Personnel affairs, recruitment, admin & government relations" },
  { id: "commercial", icon: "🏪", ar: "القطاع التجاري والتأجير والتسويق", en: "Commercial, Leasing & Marketing", descAr: "المبيعات، التأجير، التسويق، خدمة العملاء، وعلاقات المستأجرين", descEn: "Sales, leasing, marketing, customer service, and tenant relations" },
  { id: "corporate", icon: "📊", ar: "الاستراتيجية والخدمات المؤسسية", en: "Strategy & Corporate Services", descAr: "التخطيط الاستراتيجي، الاستثمار، والشؤون القانونية", descEn: "Strategic planning, investment, and legal affairs" },
  { id: "development", icon: "💻", ar: "تقنية المعلومات والدعم الفني", en: "Information Technology & Technical Support", descAr: "تقنية المعلومات، الشبكات، الأنظمة، والدعم الفني", descEn: "IT, networks, systems, and technical support" },
];

// Job-title keywords (Arabic + English) → suggested path id.
const PATH_KEYWORDS: Array<{ id: string; ar: string[]; en: string[] }> = [
  { id: "finance", ar: ["مالي", "محاسب", "محاسبة", "مالية", "خزينة", "موازنة"], en: ["finance", "accounting", "accountant", "financial", "treasury", "budget", "audit"] },
  { id: "procurement", ar: ["مشتريات", "شراء", "موردين", "تعاقد", "عقود"], en: ["procurement", "purchas", "vendor", "contract", "sourcing", "supply"] },
  { id: "project-management", ar: ["مشاريع", "مشروع", "هندس", "هندسة", "معماري", "مدني", "إنشائي", "بناء", "تصميم", "كهرباء", "ميكانيكا"], en: ["project", "engineer", "architect", "civil", "mep", "construction", "design", "pmo"] },
  { id: "assets-management", ar: ["أصول", "صيانة", "مرافق", "فاسلتي", "سلامة", "أمن", "حراسة", "عقار"], en: ["asset", "facility", "maintenance", "real estate", "property", "safety", "security"] },
  { id: "hr-admin", ar: ["موارد بشرية", "موظفين", "توظيف", "إداري", "شؤون", "بشرية", "حكومية", "علاقات حكومية"], en: ["hr", "human resources", "recruit", "admin", "personnel", "government relations", "gr "] },
  { id: "commercial", ar: ["تجاري", "مبيعات", "تأجير", "مستأجر", "بيع", "عميل", "خدمة العملاء", "مول", "تسويق"], en: ["commercial", "sales", "leasing", "tenant", "customer", "retail", "marketing"] },
  { id: "corporate", ar: ["استراتيج", "استثمار", "قانون", "علاقات عامة", "اتصال مؤسسي", "إعلام"], en: ["strategy", "invest", "legal", "communic", "public relat", "corporate", "pr "] },
  { id: "development", ar: ["تقنية المعلومات", "تقني", "دعم فني", "حاسب", "برمج", "شبك", "سيرفر", "سيستم", "تكنولوجيا"], en: ["it ", "information technology", "support", "helpdesk", "system", "network", "devops", "developer", "software", "tech"] },
];

/** Suggest a path id from a job title, or null if no keyword matches. */
export function suggestCareerPath(role: string): string | null {
  if (!role) return null;
  const r = role.toLowerCase();
  for (const entry of PATH_KEYWORDS) {
    if (entry.ar.some((k) => r.includes(k.toLowerCase())) || entry.en.some((k) => r.includes(k.toLowerCase()))) return entry.id;
  }
  return null;
}

// --- Runtime store (admin can override the list via the "paths" setting) ---
const PATHS_CHANGED = "kd-paths-changed";
let current: CareerPath[] = [...DEFAULT_CAREER_PATHS];

export function getCareerPaths(): CareerPath[] {
  return current;
}

export function subscribeCareerPaths(cb: () => void): () => void {
  window.addEventListener(PATHS_CHANGED, cb);
  return () => window.removeEventListener(PATHS_CHANGED, cb);
}

/** Load any admin-configured path overrides (public read, or admin read when signed in). */
export async function loadCareerPaths(): Promise<void> {
  const res = getAdminToken() ? await getSetting<CareerPath[]>("paths") : await getPublic<{ value?: CareerPath[] } | CareerPath[]>("paths");
  const value = Array.isArray((res.data as any)?.value) ? (res.data as any).value : res.data;
  if (Array.isArray(value) && value.length > 0) current = value as CareerPath[];
  try {
    window.dispatchEvent(new Event(PATHS_CHANGED));
  } catch {
    /* ignore */
  }
}

// --- Admin editing ---
export function newCareerPath(): CareerPath {
  return { id: "", icon: "📁", ar: "", en: "", descAr: "", descEn: "" };
}
export function isBuiltInPath(id: string): boolean {
  return DEFAULT_CAREER_PATHS.some((p) => p.id === id);
}
export async function saveCareerPaths(paths: CareerPath[]): Promise<{ ok: boolean; error?: string }> {
  current = paths;
  try {
    window.dispatchEvent(new Event(PATHS_CHANGED));
  } catch {
    /* ignore */
  }
  const res = await putSetting("paths", paths);
  return res.ok ? { ok: true } : { ok: false, error: res.error };
}
export function resetCareerPaths(): Promise<{ ok: boolean; error?: string }> {
  return saveCareerPaths([...DEFAULT_CAREER_PATHS]);
}
